import { holdoutLine, reachLine } from './overviewModel';
import { cardStyle, Dot, InfoLine, text14 } from './ui';

import { JourneysPayload } from '@/services/lifecycleAPI';

// Engine constants shown on the Journey guardrails card. They live in graphy
// and are not in the payload:
// - Lifecycle::SendDecision DAILY_CAP 1, WEEKLY_CAP 4, WEEKLY_SMS_CAP 3
// - Lifecycle::LocalTime::WINDOWS: SMS 8 AM to 8 PM, push 8 AM to 9 PM, in
//   the worker's time zone (the design's "9 PM to 8 AM" is push only)
// - Lifecycle::StopRateMonitorService::THRESHOLD 0.02
const DAILY_CAP = 1;
const WEEKLY_CAP = 4;
const WEEKLY_SMS_CAP = 3;
const STOP_RATE_PCT = 2;

const cardBox = { ...cardStyle, gap: 12, ...text14 };
const cardTitle = { fontWeight: 800, fontSize: 16 } as const;

const ReachRow = ({ label, value }: { label: string; value: string }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
    <span style={{ fontWeight: 600 }}>{label}</span>
    <span>{value}</span>
  </div>
);

const SummaryCards = ({ data }: { data: JourneysPayload }) => {
  const reach = data.global.channel_reach;
  const total = reach?.total;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 20,
      }}
    >
      <section
        aria-label="Channel reach"
        data-testid="channelReachCard"
        style={cardBox}
      >
        <div style={cardTitle}>Channel reach</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ReachRow
            label="Push enabled"
            value={reach ? reachLine(reach.push_enabled, total) : '—'}
          />
          <ReachRow
            label="SMS opted in"
            value={reach ? reachLine(reach.sms_opted_in, total) : '—'}
          />
          <ReachRow
            label="Email deliverable"
            value={reach ? reachLine(reach.email_deliverable, total) : '—'}
          />
        </div>
      </section>

      <section
        aria-label="Journey guardrails"
        data-testid="guardrailsSummaryCard"
        style={cardBox}
      >
        <div style={cardTitle}>Journey guardrails</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>Messages</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span>
                <b>Day:</b> max {DAILY_CAP}
              </span>
              <Dot />
              <span>
                <b>Week:</b> max {WEEKLY_CAP}
              </span>
              <Dot />
              <span>
                <b>SMS:</b> max {WEEKLY_SMS_CAP} a week
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>Quiet hours</span>
            <span>
              SMS 8 PM to 8 AM, push 9 PM to 8 AM, worker’s local time
            </span>
          </div>
        </div>
        {/* Mirrors SendDecision#choose_channel (review J-06). */}
        <InfoLine>
          Each step uses its own channel. If that is not allowed, it uses the
          next of SMS, push and email that the step has copy for. Messages sent
          on several channels, and push-only ones, never switch.
        </InfoLine>
      </section>

      <section
        aria-label="Measurement"
        data-testid="measurementCard"
        style={cardBox}
      >
        <div style={cardTitle}>Measurement</div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            flexGrow: 1,
            alignContent: 'flex-start',
          }}
        >
          <span style={{ paddingRight: 8 }}>{holdoutLine(data.journeys)}</span>
          <span style={{ paddingRight: 8 }}>
            <span style={{ fontWeight: 600 }}>Goal:</span> step completed within
            the window, not opens
          </span>
        </div>
        <InfoLine>
          Pause SMS if a journey’s STOP rate passes <b>{STOP_RATE_PCT}%</b> with
          50+ SMS in 7 days
        </InfoLine>
      </section>
    </div>
  );
};

export default SummaryCards;
